import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface TermsOfUseModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function TermsOfUseModal({
  isOpen,
  onClose
}: TermsOfUseModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <AnimatePresence data-id="element-244">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-245">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" data-id="element-246" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="terms-modal-title" data-id="element-247">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10" data-id="element-248">
              <h2 id="terms-modal-title" className="text-xl font-bold text-slate-900" data-id="element-249">
                Términos y Condiciones de Uso
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors" aria-label="Cerrar" data-id="element-250">
                <X size={20} data-id="element-251" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="overflow-y-auto p-6 md:p-8 text-slate-600 space-y-8" data-id="element-252">
              <p className="text-lg font-semibold text-slate-800" data-id="element-253">
                Términos y Condiciones de Uso Plataformas TicketMaestro
              </p>

              <section data-id="element-254">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-255">
                  1. ACEPTACIÓN DE LOS TÉRMINOS
                </h3>
                <p className="leading-relaxed mb-3" data-id="element-256">
                  Al acceder, navegar, registrarse o utilizar el sitio web,
                  aplicación móvil o cualquier plataforma digital oficial de
                  TicketMaestro SAS (en adelante nombrados como la "Plataforma"
                  y "TicketMaestro" respectivamente), el usuario declara haber
                  leído, entendido y aceptado íntegramente los presentes
                  Términos y Condiciones, así como la Política de Privacidad
                  vigente.
                </p>
                <p className="leading-relaxed" data-id="element-257">
                  Si el usuario no está de acuerdo con estos Términos, deberá
                  abstenerse de utilizar la Plataforma.
                </p>
              </section>

              <section data-id="element-258">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-259">
                  2. IDENTIFICACIÓN DEL TITULAR
                </h3>
                <p className="leading-relaxed" data-id="element-260">
                  La Plataforma es operada por TicketMaestro, sociedad
                  constituida conforme a las leyes de la República del Ecuador.
                </p>
              </section>

              <section data-id="element-261">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-262">
                  3. USUARIOS
                </h3>
                <p className="mb-3" data-id="element-263">Pueden utilizar la Plataforma:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-264">
                  <li data-id="element-265">Personas naturales mayores de 18 años</li>
                  <li data-id="element-266">
                    Menores de edad bajo la responsabilidad expresa de su padre,
                    madre o representante legal, especialmente en el caso de
                    escuelas deportivas
                  </li>
                </ul>
                <p className="leading-relaxed" data-id="element-267">
                  El usuario es responsable de la veracidad de la información
                  proporcionada durante su registro.
                </p>
              </section>

              <section data-id="element-268">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-269">
                  4. SERVICIOS OFRECIDOS
                </h3>
                <p className="mb-3" data-id="element-270">La Plataforma permite, entre otros:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-271">
                  <li data-id="element-272">
                    Administración de organizaciones, aliados, socios y
                    adherentes.
                  </li>
                  <li data-id="element-273">Administración y venta de productos y servicios.</li>
                  <li data-id="element-274">
                    Administración de eventos, incluyendo venta de entradas.
                  </li>
                  <li data-id="element-275">
                    Inscripción para eventos, capacitaciones, actividades u
                    otros.
                  </li>
                  <li data-id="element-276">
                    Pago de mensualidades, matrículas y otros valores
                    relacionados con productos y servicios.
                  </li>
                  <li data-id="element-277">
                    Acceso a contenido informativo, promocional y multimedia.
                  </li>
                  <li data-id="element-278">
                    Administración, gestión y operación de programas de
                    fidelización.
                  </li>
                  <li data-id="element-279">Otros productos y servicios.</li>
                </ul>
                <p className="leading-relaxed" data-id="element-280">
                  TicketMaestro se reserva el derecho de modificar, suspender o
                  eliminar servicios sin previo aviso.
                </p>
              </section>

              <section data-id="element-281">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-282">
                  5. REGISTRO Y CUENTAS
                </h3>
                <p className="mb-3" data-id="element-283">
                  Para acceder a ciertos servicios, el usuario deberá crear una
                  cuenta proporcionando información veraz y actualizada.
                </p>
                <p className="mb-3" data-id="element-284">El usuario es responsable de:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-285">
                  <li data-id="element-286">
                    Mantener la confidencialidad de su usuario y contraseña.
                  </li>
                  <li data-id="element-287">Todas las actividades realizadas desde su cuenta.</li>
                </ul>
                <p className="leading-relaxed" data-id="element-288">
                  TicketMaestro no será responsable por el uso no autorizado de
                  cuentas por negligencia del usuario.
                </p>
              </section>

              <section data-id="element-289">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-290">
                  6. CONDICIONES DE VENTA – PRODUCTOS Y SERVICIOS
                </h3>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-291">
                  <li data-id="element-292">
                    Los productos y servicios están sujetos a disponibilidad de
                    stock.
                  </li>
                  <li data-id="element-293">
                    Los precios incluyen los impuestos aplicables conforme a la
                    ley ecuatoriana.
                  </li>
                  <li data-id="element-294">
                    Las imágenes son referenciales; pueden existir variaciones
                    mínimas.
                  </li>
                  <li data-id="element-295">
                    Cambios o devoluciones solo se aceptarán por defectos de
                    fabricación o falta de prestación de servicio, conforme a la
                    política vigente.
                  </li>
                </ul>
                <p className="leading-relaxed" data-id="element-296">
                  TicketMaestro no se responsabiliza por retrasos atribuibles a
                  terceros (courier, fuerza mayor, etc.).
                </p>
              </section>

              <section data-id="element-297">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-298">
                  7. PAGOS Y TRANSACCIONES
                </h3>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-299">
                  <li data-id="element-300">
                    Todos los pagos se procesan a través de plataformas seguras
                    de terceros.
                  </li>
                  <li data-id="element-301">
                    TicketMaestro no almacena información bancaria sensible.
                  </li>
                  <li data-id="element-302">
                    El usuario autoriza el débito de los valores
                    correspondientes a los productos y servicios adquiridos.
                  </li>
                </ul>
                <p className="leading-relaxed" data-id="element-303">
                  Cualquier reclamo deberá realizarse dentro de los plazos
                  establecidos por la ley.
                </p>
              </section>

              <section data-id="element-304">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-305">
                  8. PROPIEDAD INTELECTUAL
                </h3>
                <p className="leading-relaxed mb-3" data-id="element-306">
                  Todo el contenido de la Plataforma (logos, escudos, textos,
                  imágenes, videos, diseños, marcas y software) es propiedad
                  exclusiva de TicketMaestro o de terceros autorizados.
                </p>
                <p className="leading-relaxed" data-id="element-307">
                  Queda prohibida su reproducción, distribución o uso comercial
                  sin autorización expresa y por escrito.
                </p>
              </section>

              <section data-id="element-308">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-309">
                  9. USO PROHIBIDO
                </h3>
                <p className="mb-3" data-id="element-310">El usuario se compromete a no:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-311">
                  <li data-id="element-312">Utilizar la Plataforma con fines ilícitos.</li>
                  <li data-id="element-313">Realizar fraudes o suplantación de identidad.</li>
                  <li data-id="element-314">Introducir virus, malware o códigos dañinos.</li>
                  <li data-id="element-315">Afectar el funcionamiento de la Plataforma.</li>
                </ul>
                <p className="leading-relaxed" data-id="element-316">
                  El incumplimiento podrá resultar en la suspensión o
                  cancelación inmediata de la cuenta.
                </p>
              </section>

              <section data-id="element-317">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-318">
                  10. RESPONSABILIDAD Y LIMITACIONES
                </h3>
                <p className="mb-3" data-id="element-319">TicketMaestro no garantiza:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-320">
                  <li data-id="element-321">Disponibilidad ininterrumpida de la Plataforma.</li>
                  <li data-id="element-322">Ausencia total de errores técnicos.</li>
                </ul>
                <p className="leading-relaxed" data-id="element-323">
                  El uso de la Plataforma se realiza bajo responsabilidad
                  exclusiva del usuario.
                </p>
              </section>

              <section data-id="element-324">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-325">
                  11. PROTECCIÓN DE DATOS PERSONALES
                </h3>
                <p className="leading-relaxed" data-id="element-326">
                  El tratamiento de los datos personales se rige por la Política
                  de Privacidad de TicketMaestro y la normativa vigente en
                  Ecuador.
                </p>
              </section>

              <section data-id="element-327">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-328">
                  12. VIGENCIA Y MODIFICACIONES
                </h3>
                <p className="leading-relaxed mb-3" data-id="element-329">
                  TicketMaestro podrá modificar estos Términos en cualquier
                  momento.
                </p>
                <p className="leading-relaxed" data-id="element-330">
                  La vigencia de estos Términos y Condiciones de Uso es desde el
                  20 de marzo de 2026 y permanecerá vigente hasta que exista una
                  nueva versión que la reemplace.
                </p>
              </section>

              <section data-id="element-331">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-332">
                  13. LEGISLACIÓN Y JURISDICCIÓN
                </h3>
                <p className="leading-relaxed mb-3" data-id="element-333">
                  Estos Términos se rigen por las leyes de la República del
                  Ecuador.
                </p>
                <p className="leading-relaxed" data-id="element-334">
                  Cualquier controversia será sometida a los jueces competentes
                  de la ciudad de Guayaquil.
                </p>
              </section>

              <section data-id="element-335">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-336">
                  14. CONTACTO
                </h3>
                <p className="leading-relaxed" data-id="element-337">
                  Para consultas o reclamos, el usuario puede comunicarse a
                  través de los canales oficiales de TicketMaestro disponibles
                  en la Plataforma.
                </p>
              </section>
            </div>

            {/* Footer */}
            <div className="p-4 md:p-6 border-t border-slate-100 bg-slate-50 flex justify-end" data-id="element-338">
              <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm" data-id="element-339">
                Entendido
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}